import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import dayjs from "dayjs";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import { useCurrency } from "contexts/CurrencyContext";
import { formatCurrency } from "utils/currency";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchField from "components/SearchField";
import ScrollableTableContainer from "components/table/ScrollableTableContainer";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import { offerStatusChipSx, offerTypeChipSx } from "components/table/tableChipStyles";
import CurrencyAmount from "components/currency/CurrencyAmount";
import { brand, destructive } from "utils/designTokens";
import { pluralize } from "utils/pluralize";

import {
  Box, Grid, Button, Typography, Chip, IconButton, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Tooltip, Switch,
  CircularProgress, Stack, LinearProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from "@mui/material";

import OfferEditDialog from "./OfferEditDialog";
import { OFFER_TYPES, getOfferType } from "./offerUtils";
import { useToast } from "contexts/ToastContext";

export { OFFER_TYPES, getOfferType };

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  expired: "Expired",
  upcoming: "Upcoming"
};

/** Match SearchField outline styling in the filter bar */
const offerFilterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#fff',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': {
      borderColor: brand.border
    },
    '&:hover fieldset': {
      borderColor: brand.borderHover
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${brand.overlay}`,
      '& fieldset': {
        borderColor: 'primary.main'
      }
    }
  }
};

const getOfferStatus = (offer) => {
  const now  = dayjs();
  const from = dayjs(offer.from_datetime);
  const to   = dayjs(offer.to_datetime);
  if (!offer.status) return "inactive";
  if (now.isBefore(from)) return "upcoming";
  if (now.isAfter(to))    return "expired";
  return "active";
};

const StatCard = ({ label, value }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
    <Typography variant="h5" fontWeight={700} color="primary.main">{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Paper>
);

export default function OfferList() {
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const { showApiError } = useToast();
  const [offers,       setOffers]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState("");
  const [typeFilter,   setTypeFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editDialog, setEditDialog] = useState({ open: false, id: null, listOffer: null });
  const [delDialog, setDelDialog] = useState({ open: false, id: null, name: "" });
  const [deleting, setDeleting] = useState(false);

  const loadOffers = useCallback(async () => {
    setLoading(true);
    try {
    
    const res = await api.get("/offer/list");
setOffers(Array.isArray(res.data?.data?.offers) ? res.data.data.offers : []);
    } catch (err) {
      showApiError(err, "Failed to load offers");
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => { loadOffers(); }, [loadOffers]);

  const [updatingId, setUpdatingId] = useState(null);

  const handleToggleStatus = async (id, current) => {
    setUpdatingId(id);
    try {
      await api.patch(`/offer/manage/${id}`, { status: !current });
      const res = await api.get("/offer/list");
      setOffers(Array.isArray(res.data?.data?.offers) ? res.data.data.offers : []);
    } catch (err) {
      showApiError(err, "Status update failed");
    } finally {
      setUpdatingId(null);
    }
  };

  const openDeleteDialog = (offer) => {
    const id = offer.id || offer.offer_id;
    setDelDialog({
      open: true,
      id,
      name: offer.offer_name_en || offer.offer_name_ar || "this offer"
    });
  };

  const handleDeleteConfirm = async () => {
    if (!delDialog.id) return;
    setDeleting(true);
    try {
      await api.delete(`/offer/delete/${delDialog.id}`);
      setOffers((prev) =>
        prev.filter((o) => o.id !== delDialog.id && o.offer_id !== delDialog.id)
      );
      setDelDialog({ open: false, id: null, name: "" });
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = offers.filter((o) => {
    const matchSearch =
      o.offer_name_en?.toLowerCase().includes(search.toLowerCase()) ||
      o.offer_name_ar?.includes(search) ||
      o.description?.toLowerCase().includes(search.toLowerCase()) ||
      o.description_ar?.includes(search) ||
      o.coupon_code?.toLowerCase().includes(search.toLowerCase());
    const matchType   = typeFilter   === "all" || o.offer_type  === typeFilter;
    const matchStatus = statusFilter === "all" || getOfferStatus(o) === statusFilter;
    return matchSearch && matchType && matchStatus;
  });

  const stats = {
    total:   offers.length,
    active:  offers.filter((o) => getOfferStatus(o) === "active").length,
    expired: offers.filter((o) => getOfferStatus(o) === "expired").length,
    upcoming:offers.filter((o) => getOfferStatus(o) === "upcoming").length,
  };

  return (
    <Box>
      <PageHeader
        title="Offers"
        subtitle="Manage discounts and promotional offers."
        count={filtered.length}
        actionLabel="Create Offer"
        actionIcon={AddIcon}
        onAction={() => navigate("/offer/create")}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total Offers", value: stats.total },
          { label: "Active", value: stats.active },
          { label: "Upcoming", value: stats.upcoming },
          { label: "Expired", value: stats.expired }
        ].map((s) => (
          <Grid size={{ xs: 6, md: 3 }} key={s.label}>
            <StatCard {...s} />
          </Grid>
        ))}
      </Grid>

      <MainCard
        contentSX={{ p: 0 }}
        sx={{ borderRadius: 3, position: "relative" }}
      >
        {(loading || updatingId) && (
          <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, borderTopLeftRadius: 12, borderTopRightRadius: 12 }} />
        )}
        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <SearchField
            placeholder="Search offers…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            sx={{ minWidth: 240, mb: 0 }}
          />
          <TextField
            select size="small" label="Type" value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 160, ...offerFilterFieldSx }}
          >
            <MenuItem value="all">All Types</MenuItem>
            {OFFER_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 140, ...offerFilterFieldSx }}
          >
            {["all", "active", "upcoming", "expired", "inactive"].map((s) => (
              <MenuItem key={s} value={s}>{s === "all" ? "All Status" : STATUS_LABELS[s] || s}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <ScrollableTableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "grey.50", whiteSpace: "nowrap" } }}>
                    <TableCell>Offer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Discount</TableCell>
                    <TableCell>Validity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        No offers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((offer) => {
                      const id     = offer.id || offer.offer_id;
                      const ot     = getOfferType(offer.offer_type);
                      const status = getOfferStatus(offer);
                      const statusLabel = STATUS_LABELS[status] || status;
                      return (
                        <TableRow key={id} hover sx={{ "& td": { py: 1.5 } }}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{offer.offer_name_en}</Typography>
                              {offer.offer_name_ar && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ direction: 'rtl' }}>
                                  {offer.offer_name_ar}
                                </Typography>
                              )}
                              {offer.description && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  {offer.description}
                                </Typography>
                              )}
                              {offer.description_ar && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ direction: 'rtl' }}>
                                  {offer.description_ar}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={ot.label} size="small" variant="outlined" sx={offerTypeChipSx} />
                          </TableCell>
                          <TableCell>
                            {offer.offer_value_type === "percentage" ? (
                              <Typography variant="body2" fontWeight={700} sx={{ color: brand.primary }}>
                                {offer.offer_value}%
                              </Typography>
                            ) : (
                              <CurrencyAmount
                                value={offer.offer_value}
                                variant="body2"
                                fontWeight={700}
                                color="primary.main"
                              />
                            )}
                            <Typography variant="body2" fontWeight={700} sx={{ color: brand.primary }}>
                              {offer.offer_value_type === "percentage" ? `${offer.offer_value}%` : formatCurrency(offer.offer_value, currency.code, currency.symbol)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {offer.coupon_code
                              ? <Chip label={offer.coupon_code} size="small" variant="outlined" sx={couponCodeChipSx} />
                              : "—"}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Typography variant="caption">
                              {dayjs(offer.from_datetime).format("DD MMM YY, hh:mm A")}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              → {dayjs(offer.to_datetime).format("DD MMM YY, hh:mm A")}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {offer.minimum_purchase_amount ? formatCurrency(offer.minimum_purchase_amount, currency.code, currency.symbol) : "—"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={offerStatusChipSx(status)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {updatingId === id ? (
                              <CircularProgress size={20} sx={{ color: brand.primary }} />
                            ) : (
                              <Switch
                                size="small"
                                checked={!!offer.status}
                                onChange={() => handleToggleStatus(id, offer.status)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: brand.primary,
                                    '&:hover': { bgcolor: 'rgba(31, 91, 64, 0.08)' }
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    bgcolor: brand.primary
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <TableActions>
                              <TableActionButton
                                action="edit"
                                onClick={() => setEditDialog({ open: true, id, listOffer: offer })}
                              />
                              <TableActionButton
                                action="delete"
                                onClick={() => openDeleteDialog(offer)}
                              />
                            </TableActions>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                  {filtered.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 1.5, border: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          All {filtered.length} {pluralize(filtered.length, "offer")} loaded
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollableTableContainer>
          </>
        )}
      </MainCard>

      <OfferEditDialog
        open={editDialog.open}
        offerId={editDialog.id}
        listOffer={editDialog.listOffer}
        onClose={() => setEditDialog({ open: false, id: null, listOffer: null })}
        onSaved={loadOffers}
      />

      <Dialog
        open={delDialog.open}
        onClose={() => !deleting && setDelDialog({ open: false, id: null, name: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Offer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{delDialog.name}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setDelDialog({ open: false, id: null, name: "" })}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": { borderColor: brand.borderHover, bgcolor: brand.overlay }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{
              bgcolor: destructive.main,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: destructive.shadow,
              "&:hover": { bgcolor: destructive.dark, boxShadow: destructive.shadow },
              "&.Mui-disabled": { bgcolor: "rgba(183, 28, 28, 0.45)", color: "#fff" }
            }}
          >
            {deleting ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}